import "ag-charts-enterprise";
import { AgChartsReact } from "ag-charts-react";
import "ag-grid-enterprise";
import { useCallback, useEffect, useState } from "react";
import ForecastReview from "../../../../models/TradeAndPromoPlanning/ForecastReview.model";
import { getSortedUSADateStrList } from "../../../../shared/functions";

interface ChartData {
  week_start_date: string;
  prev_year_shipment_history: number;
  ibp_stat_forecast: number;
  final_consumption_forecast: number;
};

export const ForecastReviewChart = (props: {
  chartHeight: string;
  gridRowData: ForecastReview[];
}) => {
  const [chartOptions, setChartOptions] = useState<any>({});

  const getChartData = useCallback((gridRowData: ForecastReview[]): ChartData[] => {
    const chartData: ChartData[] = [];
    const requiredCols: string[] = [
      "prev_year_shipment_history",
      "ibp_stat_forecast",
      "final_consumption_forecast",
      "adjusted_baseline_forecast",
      "final_shipment_forecast"
    ];

    if (gridRowData.length > 0) {
      const usaDateStrList: string[] = Object.keys(gridRowData[0]).filter((key: string) => Boolean(Date.parse(key)));
      const sortedDateStrList: string[] = getSortedUSADateStrList(usaDateStrList);

      const weekStartDateMap: { [key: string]: any; } = {};
      sortedDateStrList.forEach((usaDateStr: string) => {
        weekStartDateMap[usaDateStr] = {};
      });

      gridRowData.filter(row => requiredCols.includes(row.variable)).forEach((rowObj: ForecastReview) => {
        const rowColName: string = rowObj.variable;
        for (let key in rowObj) {
          if (key !== "variable" && key !== "rowIndex") {
            console.log("key: ", key)
            weekStartDateMap[key][rowColName] = rowObj[key];
          }
        }
      });

      for (let usaDateStr in weekStartDateMap) {
        chartData.push({
          week_start_date: usaDateStr,
          ...weekStartDateMap[usaDateStr]
        });
      }
    }

    return chartData;
  }, []);

  const graphRenderer = (params: any) => {
    const formatter = new Intl.NumberFormat("en-US");
    const { datum, xKey, yKey } = params;
    return {
      title: `Week Start Date: ${datum[xKey]}`,
      content: formatter.format(datum[yKey]),
    };
  };

  const getChartOptions = useCallback((chartData: any[]): any => {
    const formatter = new Intl.NumberFormat("en-US");
    // Round the sales data before passing to the chart
    const roundedData = chartData.map(item => ({
      adjusted_baseline_forecast: Math.round(item.adjusted_baseline_forecast),
      final_consumption_forecast: Math.round(item.final_consumption_forecast),
      ibp_stat_forecast: Math.round(item.ibp_stat_forecast),
      prev_year_shipment_history: Math.round(item.prev_year_shipment_history),
      final_shipment_forecast: Math.round(item.final_shipment_forecast),
      week_start_date: item.week_start_date
    }));

    return {
      data: roundedData,
      series: [
        {
          type: "area",
          xKey: "week_start_date",
          yKey: "ibp_stat_forecast",
          yName: "IBP Forecast",
          tooltip: {
            renderer: graphRenderer,
          },
          stroke: "lightGreen",
          fill: "lightGreen"
        },
        {
          type: "line",
          xKey: "week_start_date",
          yKey: "prev_year_shipment_history",
          yName: "PY Shipments",
          tooltip: {
            renderer: graphRenderer,
          },
          lineDash: [3, 4],
          stroke: "indigo",
          marker: {
            fill: "indigo"
          }
        },
        {
          type: "line",
          xKey: "week_start_date",
          yKey: "adjusted_baseline_forecast",
          yName: "Adjusted Baseline Forecast",
          tooltip: {
            renderer: graphRenderer,
          },
          stroke: "teal",
          marker: {
            fill: "teal"
          }
        },
        {
          type: "line",
          xKey: "week_start_date",
          yKey: "final_consumption_forecast",
          yName: "Final Consumption Forecast",
          tooltip: {
            renderer: graphRenderer,
          },
          stroke: "#E3170A",
          marker: {
            fill: "#E3170A"
          }
        },
        {
          type: "line",
          xKey: "week_start_date",
          yKey: "final_shipment_forecast",
          yName: "Final Shipment Forecast",
          tooltip: {
            renderer: graphRenderer,
          },
          stroke: "#ffd700",
          marker: {
            fill: "#ffd700"
          }
        },
      ],
      zoom: {
        enabled: true,
        buttons: {
          enabled: false,
        },
      },
      axes: [
        {
          type: "category",
          position: "bottom",
          keys: ["week_start_date"],
          nice: true,
          label: {
            format: "%-m/%-d/%Y",
            avoidCollisions: true,
            rotation: 0,
          },
          title: {
            enabled: false,
            text: "Week Start Date"
          },
          tick: {
            enabled: true,
            size: 15,
          },
        },
        {
          type: "number",
          position: "left",
          keys: ["ibp_stat_forecast", "prev_year_shipment_history", "final_consumption_forecast", "final_shipment_forecast", "adjusted_baseline_forecast"],
          title: {
            enabled: false,
            text: "Final Consumption Forecast",
          },
          label: {
            formatter: (params: any) => formatter.format(params.value), // Format the Y-axis labels with units
          },
        }
      ]
    };
  }, []);

  useEffect(() => {
    const chartData: ChartData[] = getChartData(props.gridRowData);
    setChartOptions((prevOptions: any) => getChartOptions(chartData));
  }, [props.gridRowData, getChartData, getChartOptions]);

  return (
    <>
      {/* <AgCharts options={options} /> */}
      <div
        style={{
          height: props.chartHeight,
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <AgChartsReact options={chartOptions} />
      </div>
    </>
  );
};